import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHideAndSaveProjectInteraction } from 'hooks/useHideAndSaveProjectInteraction';
import HiddenProjectsAPI from 'services/API/HiddenProjectsAPI';
import { Project, PaginatedProject } from 'types';
import { trackSaveEvent } from 'utils/analytics';

type TProjectSlug = Project['slug'];

type THideProjectInput = {
  projectSlug?: TProjectSlug;
  documentId?: string;
  currentProjectCategory?: string;
};

type TPaginatedHiddenProjects = {
  time: number;
  count: number;
  results: PaginatedProject[];
};

type TAllHiddenSet = Set<string>;

type TPaginatedProps = {
  pageNumber?: number;
  sort?: string[];
};

export const useHiddenProjects = () => {
  const queryClient = useQueryClient();
  const {
    optimisticallyRemoveSavedProject,
    optimisticallyAddHiddenProject,
    optimisticallyRemoveHiddenProject,
    actionSavedProjectTriggerRefetch,
    removeHiddenProjectTriggerRefetch,
  } = useHideAndSaveProjectInteraction();

  const getPaginatedHiddenProjectsForUser = ({
    pageNumber = 1,
    sort = [],
  }: TPaginatedProps) =>
    useQuery(
      ['hidden-projects-list', sort, pageNumber],
      async (): Promise<TPaginatedHiddenProjects | null> => {
        const result = await HiddenProjectsAPI.getPaginatedHiddenProjects(
          pageNumber,
          sort,
        );

        return result?.data || null;
      },
      {
        refetchOnWindowFocus: false,
      },
    );

  const hideProject = useMutation({
    mutationFn: ({ projectSlug, documentId }: THideProjectInput) =>
      HiddenProjectsAPI.postHideProject({ projectSlug, documentId }),
    onMutate: async ({
      projectSlug,
      documentId,
      currentProjectCategory,
    }: THideProjectInput) => {
      if (!projectSlug && !documentId) {
        return;
      }
      optimisticallyRemoveSavedProject(projectSlug || documentId || '');
      optimisticallyAddHiddenProject(projectSlug || documentId || '');
      trackSaveEvent({
        projectId: projectSlug,
        saveProjectNewState: 'hide',
        saveProjectPastState: currentProjectCategory,
      });
    },
    onSettled: () => {
      actionSavedProjectTriggerRefetch();
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['hidden-projects'] });
    },
  });

  const getAllHiddenProjectIdsForUser = useQuery(
    ['hidden-projects'],
    async (): Promise<TAllHiddenSet> => {
      const result = await HiddenProjectsAPI.getHiddenProjectIds();

      return new Set(result?.data?.data || []);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !hideProject?.isLoading,
      staleTime: 50,
    },
  );

  const unhideProject = useMutation({
    mutationFn: (projectSlug?: TProjectSlug) =>
      HiddenProjectsAPI.deleteHiddenProject(projectSlug as string),
    onMutate: async (projectSlug: TProjectSlug) => {
      optimisticallyRemoveHiddenProject(projectSlug);
      trackSaveEvent({
        projectId: projectSlug,
        saveProjectNewState: 'uncategorized',
      });
    },
    onSettled: () => {
      removeHiddenProjectTriggerRefetch();
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['hidden-projects'] });
    },
  });

  const isProjectHidden = useCallback(
    (projectSlug: TProjectSlug): boolean =>
      !!getAllHiddenProjectIdsForUser?.data?.has(projectSlug),
    [getAllHiddenProjectIdsForUser.data],
  );

  return {
    getPaginatedHiddenProjectsForUser,
    getAllHiddenProjectIdsForUser,
    optimisticallyRemoveHiddenProject,
    hideProject,
    unhideProject,
    isProjectHidden,
  };
};
